<template>
  <div
    class="file-tile"
    :class="{ selected: isSelected, 'selection-mode': selectionMode, 'other-branch-item': isOtherBranch(item.branchId) }"
  >
    <div class="tile-frame" :class="{ dark: isDicom }">
      <button
        type="button"
        class="tile-open"
        :aria-label="selectionMode ? `เลือก ${label}` : `ดู ${label}`"
        @click="onTileClick"
        @touchstart.passive="startLongPress"
        @touchend="cancelLongPress"
        @touchmove.passive="cancelLongPress"
        @contextmenu="onContextMenu"
      >
        <div v-if="item.isDeleting" class="tile-center">
          <b-spinner small variant="danger" label="กำลังลบ" />
        </div>
        <div v-else-if="isPdf" class="tile-center tile-pdf">
          <i class="far fa-file-pdf" aria-hidden="true"></i>
          <span>PDF</span>
        </div>
        <div v-else-if="isDicom" class="tile-center">
          <img v-if="dicomThumbnail" :src="dicomThumbnail" :alt="label" class="tile-img contain" />
          <span v-else class="tile-dicom-label">DICOM</span>
        </div>
        <MediaThumb v-else :item="item" :alt="label" class="tile-img" />
      </button>

      <button
        type="button"
        class="tile-check"
        :aria-label="isSelected ? `ยกเลิกเลือก ${label}` : `เลือก ${label}`"
        :aria-pressed="isSelected ? 'true' : 'false'"
        @click.stop="$emit('select', item)"
      >
        <i class="fas fa-check" aria-hidden="true"></i>
      </button>

      <b-dropdown
        size="sm"
        variant="link"
        no-caret
        right
        class="tile-menu"
        toggle-class="tile-menu-toggle"
        @click.stop
      >
        <template #button-content>
          <i class="fas fa-ellipsis-v" aria-hidden="true"></i>
          <span class="sr-only">ตัวเลือกเพิ่มเติม</span>
        </template>
        <b-dropdown-item @click.stop="$emit('download', item)">
          <i class="fas fa-download mr-2"></i> ดาวน์โหลด
        </b-dropdown-item>
        <b-dropdown-item
          v-if="isPdf"
          :link-class="{ 'text-muted': !canGenerateDocument }"
          @click.stop="handleSign"
        >
          <i class="fas fa-signature mr-2"></i> เซ็นเอกสาร
          <UpgradeBadge v-if="!canGenerateDocument" class="ml-1" />
        </b-dropdown-item>
        <b-dropdown-item v-if="item.category === 'XRay'" @click.stop="$emit('radiiView', item)">
          <i class="fas fa-x-ray mr-2"></i> RadiiView
        </b-dropdown-item>
        <b-dropdown-item v-if="isDicom" @click.stop="$emit('preview', item)">
          <i class="fas fa-eye mr-2"></i> DICOM Viewer
        </b-dropdown-item>
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-item variant="danger" @click.stop="$emit('delete', item)">
          <i class="fas fa-trash-alt mr-2"></i> ลบ
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="tile-caption">
      <span class="tile-remark" :title="item.remark">{{ item.remark || "ไม่มีคำอธิบาย" }}</span>
      <span class="tile-date">
        {{ dateText }}
        <span v-if="isOtherBranch(item.branchId)" class="branch-badge branch-badge-sm">
          {{ (item.branch && item.branch.name) || "สาขาอื่น" }}
        </span>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import formatDate from "@/utils/formatDate";
import { isDicomFile, createDicomThumbnail } from "@/utils/daikonUtils";
import CrossBranchMixin from "@/mixins/CrossBranchMixin";
import UpgradeNoticeMixin from "@/mixins/UpgradeNoticeMixin";
import MediaThumb from "@/components/common/MediaThumb";
import UpgradeBadge from "@/components/badge/UpgradeBadge.vue";

const LONG_PRESS_MS = 450;

/**
 * One file in a category grid. Tapping opens it; once anything is selected
 * (selectionMode) tapping selects instead. On touch screens a long press
 * starts selecting.
 */
export default {
  name: "FileItem",
  components: { MediaThumb, UpgradeBadge },
  mixins: [CrossBranchMixin, UpgradeNoticeMixin],
  props: {
    item: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    selectionMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dicomThumbnail: null,
      pressTimer: null,
      pressed: false,
    };
  },
  computed: {
    ...mapGetters({
      getTreatmentHistoryList: "moduleTreatment/getTreatmentHistoryList",
      canGenerateDocument: "moduleBranch/canGenerateDocument",
    }),
    isDicom() {
      return isDicomFile(this.item);
    },
    isPdf() {
      return this.item.fileType === "application/pdf";
    },
    label() {
      return this.item.remark || this.item.category || "ไฟล์";
    },
    // The day it was treated when the file belongs to a treatment, otherwise
    // the day it was uploaded.
    dateText() {
      const treatment =
        this.item.treatmentId &&
        (this.getTreatmentHistoryList || []).find(t => t.id === this.item.treatmentId);
      return treatment
        ? formatDate(treatment.creationDt)
        : `อัปโหลด ${formatDate(this.item.creationDt)}`;
    },
  },
  mounted() {
    if (this.isDicom) this.generateDicomThumbnail();
  },
  beforeDestroy() {
    clearTimeout(this.pressTimer);
  },
  methods: {
    async generateDicomThumbnail() {
      try {
        const url = await createDicomThumbnail(this.item.src || this.item.url);
        if (url) this.dicomThumbnail = url;
      } catch (error) {
        console.warn("Failed to generate DICOM thumbnail:", error);
      }
    },
    handleSign() {
      if (!this.canGenerateDocument) {
        this.showUpgradeNotice("การเซ็นเอกสารเก็บเข้าแฟ้มคนไข้");
        return;
      }
      this.$emit("sign", this.item);
    },
    onTileClick() {
      // A long press already selected it; the click that follows is not a tap.
      if (this.pressed) {
        this.pressed = false;
        return;
      }
      this.$emit(this.selectionMode ? "select" : "preview", this.item);
    },
    startLongPress() {
      this.pressed = false;
      clearTimeout(this.pressTimer);
      this.pressTimer = setTimeout(() => {
        this.pressed = true;
        if (!this.isSelected) this.$emit("select", this.item);
      }, LONG_PRESS_MS);
    },
    cancelLongPress() {
      clearTimeout(this.pressTimer);
    },
    onContextMenu(event) {
      // The long press would otherwise also open the phone's image menu.
      if (this.pressed) event.preventDefault();
    },
  },
};
</script>

<style scoped>
.file-tile {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}
.tile-frame {
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 14px;
  overflow: hidden;
  background: #f1f3f6;
  transition: box-shadow 0.15s, transform 0.15s;
}
.tile-frame:hover {
  box-shadow: 0 6px 16px rgba(16, 24, 40, 0.18);
}
.tile-frame.dark {
  background: #1d2330;
}
.file-tile.selected .tile-frame {
  box-shadow: 0 0 0 3px var(--primary, #0b63c5);
}
.tile-open {
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  display: block;
  -webkit-touch-callout: none;
  user-select: none;
}
.tile-open:focus-visible {
  outline: 3px solid var(--primary, #0b63c5);
  outline-offset: -3px;
}
.tile-img,
.tile-open ::v-deep .media-thumb-placeholder,
.tile-open ::v-deep img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.tile-img.contain {
  object-fit: contain;
}
.tile-open ::v-deep .media-thumb-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}
.tile-center {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}
.tile-pdf {
  background: #fbf1f0;
  color: #b42318;
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
}
.tile-pdf i {
  font-size: 2rem;
}
.tile-dicom-label {
  color: #c9d0da;
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
}

.tile-check {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 2px solid #fff;
  border-radius: 14px;
  background: rgba(20, 24, 31, 0.35);
  color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  opacity: 0;
  transition: opacity 0.15s;
}
.tile-frame:hover .tile-check,
.tile-check:focus-visible,
.file-tile.selection-mode .tile-check,
.file-tile.selected .tile-check {
  opacity: 1;
}
.file-tile.selected .tile-check {
  border-color: var(--primary, #0b63c5);
  background: var(--primary, #0b63c5);
  color: #fff;
}

.tile-menu {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.15s;
}
.tile-frame:hover .tile-menu,
.tile-menu:focus-within,
.tile-menu.show {
  opacity: 1;
}
.tile-menu ::v-deep .tile-menu-toggle {
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.95);
  color: #101828;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.2);
}

.tile-caption {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.35;
}
.tile-remark {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #101828;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tile-date {
  font-size: 0.75rem;
  color: #5d6679;
}
@media (max-width: 575.98px) {
  .tile-frame {
    border-radius: 12px;
  }
}

/* จอสัมผัสไม่มี hover: ปุ่มเมนูต้องเห็นตลอด */
@media (hover: none) {
  .tile-menu {
    opacity: 1;
  }
}
</style>
